import React, { useState, useEffect, useMemo } from 'react';
import orderBy from 'lodash.orderby';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import {
  Card,
  CardHeader,
  Container,
  Row,
  Button,
  Input,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Modal,
  CardBody,
  Form,
  Col,
  Badge,
  ModalBody,
  Table,
  ModalHeader,
} from 'reactstrap';
import Header from 'components/Headers/Header.js';
import {
  FaEdit,
  FaPlusCircle,
  FaTimesCircle,
  FaCheckCircle,
  FaTable,
  FaEyeSlash,
  FaEye,
  FaList,
  FaSearch,
  FaHistory,
  FaRegListAlt,
  FaMoneyBill,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';
import api from 'services/api';
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Tooltip,
} from '@material-ui/core';
import { iTabelaGenerica } from 'models/TabelaGenerica';
import { Link } from 'react-router-dom';
import {
  AutoSizer,
  Column,
  List,
  Table as Tbl,
  SortDirection,
  SortDirectionType,
} from 'react-virtualized';
import './style.css';
import ModalLog from 'components/ModalLog';
import formatCurrency from 'utils/formatCurrency';
import { BuscarRegras } from './styles';

interface editTabelaGenerica extends iTabelaGenerica {
  labelValido: boolean;
}

export interface iMaterial {
  id: number;
  diteCodigo: string;
  fabrCodigo?: string;
  fabrEmprCodigo?: string;
  diteDgpeCodigo?: string;
  descricao?: string;
  dataUltimaIntegracao?: any;
  createdAt?: any;
  updatedAt?: any;
  isAtivo: boolean;
  codigoSap: string | null;
  integracaoId: number | null;
  descricaoFabrSge: string | null;
  fabrNome: string | null;
  fabrSap: string | null;
  unidadeMedida?: string | null;
  tipoItem?: string | null;
  tipoPesoItem?: string | null;
  peso?: number | null;
  statusSap?: string | null;
}

interface iMaterialPrecificacao {
  id: number;
  materialId: number;
  atividade: string;
  empresa: string;
  valor: number;
  updatedAt?: any;
  integracaoId: number | null;
  custo: number | null;
  custoImp: number | null;
  canalDistribuicao: string | null;
  centroLucro: string | null;
  organizacaoVenda: string | null;
  setorAtividade: string | null;
}

interface iMaterialComposicao {
  id: number;
  material: iMaterial;
  materialId: number;
  quantidade: string;
  updatedAt?: any;
  integracaoId: number | null;
}

const ptBrCollator = new Intl.Collator('pt-BR', {
  numeric: true,
  sensitivity: 'base',
});

const Materiais: React.FC = () => {
  const [listaMaterial, setListaMaterial] = useState([] as iMaterial[]);
  const [listaMaterialFiltrada, setListaMaterialFiltrada] = useState(
    [] as iMaterial[],
  );
  const [listaPrecificacao, setListaPrecificacao] = useState(
    [] as iMaterialPrecificacao[],
  );
  const [listaComposicao, setListaComposicao] = useState(
    [] as iMaterialComposicao[],
  );
  const [modalPrecificacaoVisivel, setModalPrecificacaoVisivel] =
    useState(false);
  const [modalComposicaoVisivel, setModalComposicaoVisivel] = useState(false);
  const [loadingDadosPrecificacao, setLoadingDadosPrecificacao] =
    useState(false);
  const [loadingDadosComposicao, setLoadingDadosComposicao] = useState(false);
  const [modalMaterial, setModalMaterial] = useState<iMaterial>(
    {} as iMaterial,
  );

  const [modalLogVisivel, setModalLogVisivel] = useState(false);
  const [mostrarDesativados, setMostrarDesativados] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalProp, setModalProp] = useState('');
  const [campoBusca, setCampoBusca] = useState('');
  const [debouncedCampoBusca, setDebouncedCampoBusca] = useState('');

  const [sortBy, setSortBy] = useState<string>('codigoSap');
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(
    SortDirection.ASC,
  );

  function handleSort({
    sortBy: newSortBy,
    sortDirection: newSortDirection,
  }: {
    sortBy: string;
    sortDirection: SortDirectionType;
  }) {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
  }

  const listaMaterialOrdenada = useMemo(() => {
    if (!listaMaterialFiltrada || listaMaterialFiltrada.length === 0) return [];

    const isAsc = sortDirection === SortDirection.ASC;

    // 1. Mapeamento prévio dos valores de ordenação (Schwartzian transform)
    // Reduz de O(N log N) para O(N) o acesso a propriedades e trim()
    const mapped = listaMaterialFiltrada.map(item => {
      let val: any = item[sortBy as keyof iMaterial];
      if (sortBy === 'fabrCodigo') {
        val = item.fabrSap ?? item.fabrCodigo ?? item.descricaoFabrSge;
      }
      if (val === null || val === undefined) {
        val = null;
      } else if (typeof val === 'string') {
        const trimmed = val.trim();
        val = trimmed === '' ? null : trimmed;
      }
      return { item, val };
    });

    mapped.sort((a, b) => {
      const valA = a.val;
      const valB = b.val;

      // Trata nulos: no ASC ficam no final, no DESC ficam no início
      const isNullA = valA === null;
      const isNullB = valB === null;

      if (isNullA && isNullB) return 0;
      if (isNullA) return isAsc ? 1 : -1;
      if (isNullB) return isAsc ? -1 : 1;

      if (valA === valB) return 0;

      let comparison = 0;
      if (typeof valA === 'number' && typeof valB === 'number') {
        comparison = valA - valB;
      } else if (typeof valA === 'string' && typeof valB === 'string') {
        comparison = ptBrCollator.compare(valA, valB);
      } else {
        comparison = ptBrCollator.compare(String(valA), String(valB));
      }

      return isAsc ? comparison : -comparison;
    });

    return mapped.map(m => m.item);
  }, [listaMaterialFiltrada, sortBy, sortDirection]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedCampoBusca(campoBusca);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [campoBusca]);

  async function carregarDados() {
    setLoading(true);
    const response = await api.get('/material');

    const lista = response.data as iMaterial[];

    setListaMaterial(lista);
    setLoading(false);
  }

  useEffect(() => {
    carregarDados();
  }, []);

  async function filtrar() {
    let lista = listaMaterial;

    if (debouncedCampoBusca.trim()) {
      const search = debouncedCampoBusca.trim().toUpperCase();
      lista = listaMaterial.filter(item => {
        return (
          (item.codigoSap && item.codigoSap.toUpperCase().includes(search)) ||
          (item.descricao && item.descricao.toUpperCase().includes(search)) ||
          (item.descricaoFabrSge &&
            item.descricaoFabrSge.toUpperCase().includes(search)) ||
          (item.diteCodigo && item.diteCodigo.toUpperCase().includes(search)) ||
          (item.id && String(item.id).includes(search)) ||
          (item.unidadeMedida &&
            item.unidadeMedida.toUpperCase().includes(search)) ||
          (item.tipoItem && item.tipoItem.toUpperCase().includes(search)) ||
          (item.tipoPesoItem &&
            item.tipoPesoItem.toUpperCase().includes(search)) ||
          (item.peso != null && String(item.peso).includes(search)) ||
          (item.statusSap && item.statusSap.toUpperCase().includes(search))
        );
      });
    }

    const novaLista = lista.filter(item =>
      mostrarDesativados ? true : item.isAtivo === true,
    );

    setListaMaterialFiltrada(novaLista);
  }

  useEffect(() => {
    filtrar();
  }, [listaMaterial, mostrarDesativados, debouncedCampoBusca]);

  async function mostrarModalLog(material: iMaterial) {
    setModalProp(`${material.id}`);
    setModalLogVisivel(true);
  }

  async function mostrarLogPrecificacao(material: iMaterial) {
    setLoadingDadosPrecificacao(true);
    setListaPrecificacao([]);
    setModalMaterial({} as iMaterial);

    const response = await api.get(`/material/${material.id}/precificacao`);

    const lista = response.data as iMaterialPrecificacao[];

    setModalMaterial(material);
    setListaPrecificacao(lista);
    setModalPrecificacaoVisivel(true);
    setLoadingDadosPrecificacao(false);
  }

  async function mostrarLogComposicao(material: iMaterial) {
    setLoadingDadosComposicao(true);
    setListaComposicao([]);
    setModalMaterial({} as iMaterial);

    const response = await api.get(`/material/${material.id}/composicao`);

    const lista = response.data as iMaterialComposicao[];

    setModalMaterial(material);
    setListaComposicao(lista);
    setModalComposicaoVisivel(true);
    setLoadingDadosComposicao(false);
  }

  async function filtroTabela(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    const search = event.target.value;

    setCampoBusca(search);
  }

  function customHeaderRenderer({
    label,
    dataKey,
    sortBy: currentSortBy,
    sortDirection: currentSortDirection,
    disableSort,
  }: any) {
    const isSorted = currentSortBy === dataKey;

    const renderSortIcon = () => {
      if (!isSorted) return <FaSort style={{ opacity: 0.3 }} />;
      return currentSortDirection === SortDirection.ASC ? (
        <FaSortUp />
      ) : (
        <FaSortDown />
      );
    };

    return (
      <div
        className="d-flex align-items-center"
        style={{
          width: '100%',
          gap: '4px',
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          color: isSorted ? '#5e72e4' : '#8898aa',
          userSelect: 'none',
          cursor: disableSort ? 'default' : 'pointer',
        }}
      >
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={typeof label === 'string' ? label : ''}
        >
          {label}
        </span>
        {!disableSort && (
          <span
            style={{
              display: 'inline-flex',
              flexShrink: 0,
              fontSize: '11px',
              color: isSorted ? '#5e72e4' : '#adb5bd',
            }}
          >
            {renderSortIcon()}
          </span>
        )}
      </div>
    );
  }

  function renderTabelaConteudo() {
    if (loading) {
      return (
        <Row className="my-5">
          <Col className="text-center">
            <CircularProgress />
            <div className="mt-3 text-muted">
              Carregando lista de materiais...
            </div>
          </Col>
        </Row>
      );
    }

    if (!listaMaterialOrdenada || listaMaterialOrdenada.length === 0) {
      return (
        <Row className="my-5">
          <Col className="text-center text-muted">
            <i>Nenhum material encontrado.</i>
          </Col>
        </Row>
      );
    }

    return (
      <div className="mt-4">
        <div style={{ height: '600px', width: '100%' }}>
          <AutoSizer>
            {({ height, width }) => (
              <Tbl
                gridStyle={{ outline: 'none' }}
                width={width}
                height={height}
                headerHeight={36}
                rowHeight={70}
                rowCount={listaMaterialOrdenada.length}
                rowGetter={({ index }: { index: number }) =>
                  listaMaterialOrdenada[index]
                }
                sortBy={sortBy}
                sortDirection={sortDirection}
                sort={handleSort}
              >
                <Column
                  width={70}
                  label="#"
                  dataKey="id"
                  headerRenderer={customHeaderRenderer}
                />
                <Column
                  width={100}
                  label="SAP"
                  dataKey="codigoSap"
                  headerRenderer={customHeaderRenderer}
                />
                <Column
                  width={120}
                  label="DomSGE"
                  dataKey="diteCodigo"
                  headerRenderer={customHeaderRenderer}
                />
                <Column
                  label="Descrição"
                  dataKey="descricao"
                  width={300}
                  flexGrow={1}
                  headerRenderer={customHeaderRenderer}
                  cellRenderer={({ cellData }) => (
                    <div
                      style={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        lineHeight: '1.4',
                        display: 'block',
                      }}
                    >
                      {cellData}
                    </div>
                  )}
                />

                <Column
                  width={240}
                  label="Fabricante"
                  dataKey="fabrCodigo"
                  headerRenderer={customHeaderRenderer}
                  cellRenderer={({ rowData }) => (
                    <div
                      style={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        lineHeight: '1.4',
                        display: 'block',
                      }}
                    >
                      {rowData?.fabrSap ?? rowData?.fabrCodigo} -{' '}
                      {rowData?.descricaoFabrSge}
                    </div>
                  )}
                />
                <Column
                  width={120}
                  label="Un. Medida"
                  dataKey="unidadeMedida"
                  headerRenderer={customHeaderRenderer}
                />
                <Column
                  width={110}
                  label="Tipo Item"
                  dataKey="tipoItem"
                  headerRenderer={customHeaderRenderer}
                />
                <Column
                  width={140}
                  label="Tipo Peso Item"
                  dataKey="tipoPesoItem"
                  headerRenderer={customHeaderRenderer}
                />
                <Column
                  width={90}
                  label="Peso"
                  dataKey="peso"
                  headerRenderer={customHeaderRenderer}
                />
                <Column
                  width={120}
                  label="Status SAP"
                  dataKey="statusSap"
                  headerRenderer={customHeaderRenderer}
                />
                <Column
                  label=""
                  dataKey="acoes"
                  width={140}
                  flexShrink={0}
                  disableSort
                  headerRenderer={customHeaderRenderer}
                  cellRenderer={({ rowData }) => (
                    <div className="float-right">
                      <Tooltip title="Visualizar Logs">
                        <Button
                          size="sm"
                          color="default"
                          outline
                          onClick={() => mostrarModalLog(rowData)}
                        >
                          <FaHistory />
                        </Button>
                      </Tooltip>

                      <Button
                        size="sm"
                        className="btn-icon"
                        color="info"
                        title="Visualizar dados de Precificação"
                        onClick={() => mostrarLogPrecificacao(rowData)}
                      >
                        <FaMoneyBill />
                      </Button>

                      <Button
                        size="sm"
                        className="btn-icon"
                        color="info"
                        title="Visualizar dados de Composição"
                        onClick={() => mostrarLogComposicao(rowData)}
                      >
                        <FaRegListAlt />
                      </Button>
                    </div>
                  )}
                />
              </Tbl>
            )}
          </AutoSizer>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header showCards={false} />
      {/* Page content */}

      <ModalLog
        nomeChave="material_id"
        valorChave={modalProp}
        titulo="Logs"
        modalState={modalLogVisivel}
        toggle={() => setModalLogVisivel(false)}
      />

      <Modal
        className="modal-dialog-centered"
        size="xl"
        style={{ maxWidth: '80vw', width: '100%' }}
        isOpen={modalPrecificacaoVisivel}
        toggle={() => setModalPrecificacaoVisivel(false)}
      >
        <ModalBody>
          <h3 className="mb-0">
            Detalhes de Precificação do material {modalMaterial.diteCodigo}
          </h3>
          {loadingDadosPrecificacao && (
            <>Buscando dados da precificação do material</>
          )}
          {!loadingDadosPrecificacao && (
            <>
              <Table
                className="align-items-center table-flush table-striped mt-1"
                responsive
              >
                <thead>
                  <tr>
                    <th>Canal Distrib.</th>
                    <th>Centro Lucro</th>
                    <th>Org. Venda</th>
                    <th>Setor Ativ.</th>
                    <th>Valor</th>
                    <th>Custo</th>
                    <th>Custo Imp</th>
                    <th>Integração</th>
                  </tr>
                </thead>
                <tbody>
                  {listaPrecificacao.map(
                    (registro: iMaterialPrecificacao, index: number) => {
                      return (
                        <tr key={`id_${index}`}>
                          <td>{registro.canalDistribuicao} </td>
                          <td>{registro.centroLucro} </td>
                          <td>{registro.organizacaoVenda} </td>
                          <td>{registro.setorAtividade} </td>
                          <td>{formatCurrency(registro.valor ?? 0)} </td>
                          <td>{formatCurrency(registro.custo ?? 0)} </td>
                          <td>{formatCurrency(registro.custoImp ?? 0)} </td>
                          <td>
                            #{registro.integracaoId ?? null}
                            {' em '}
                            {new Intl.DateTimeFormat('pt-BR', {
                              dateStyle: 'short',
                              timeStyle: 'medium',
                            }).format(new Date(registro.updatedAt))}
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </Table>
            </>
          )}
        </ModalBody>
      </Modal>

      <Modal
        className="modal-dialog-centered"
        size="xl"
        isOpen={modalComposicaoVisivel}
        style={{ maxWidth: '80vw', width: '100%' }}
        toggle={() => setModalComposicaoVisivel(false)}
      >
        <ModalBody>
          <h3 className="mb-0">
            Detalhes da Composição do material {modalMaterial.diteCodigo}
          </h3>
          {loadingDadosComposicao && (
            <>Buscando dados da composição do material</>
          )}
          {!loadingDadosComposicao && (
            <>
              <Table
                className="align-items-center table-flush table-striped mt-1"
                responsive
              >
                <thead>
                  <tr>
                    <th>#</th>
                    <th>SAP</th>
                    <th>DomSGE</th>
                    <th>Empresa</th>
                    <th>Descrição</th>
                    <th>Quantidade</th>
                    <th>Integração</th>
                  </tr>
                </thead>
                <tbody>
                  {listaComposicao.map(
                    (registro: iMaterialComposicao, index: number) => {
                      return (
                        <tr key={`id_${index}`}>
                          <td>{registro.material.id} </td>
                          <td>{registro.material.codigoSap ?? ''} </td>
                          <td>{registro.material.diteCodigo} </td>
                          <td>{registro.material.fabrEmprCodigo ?? ''} </td>
                          <td>{registro.material.descricao}</td>
                          <td>{registro.quantidade} </td>
                          <td>
                            #{registro.integracaoId ?? null}
                            {' em '}
                            {new Intl.DateTimeFormat('pt-BR', {
                              dateStyle: 'short',
                              timeStyle: 'medium',
                            }).format(new Date(registro.updatedAt))}
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </Table>
            </>
          )}
        </ModalBody>
      </Modal>

      <Container className="mt--7" fluid>
        {/* Table */}
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row>
                  <Col sm="6">
                    <h3 className="mb-0">Lista de Materiais</h3>
                  </Col>
                  <Col sm="6" hidden>
                    <FormControlLabel
                      className="float-right"
                      control={
                        <Checkbox
                          checked={mostrarDesativados}
                          onChange={event =>
                            setMostrarDesativados(event.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="Mostrar desativados"
                    />
                  </Col>
                </Row>
              </CardHeader>

              <Row>
                <Col sm="8">
                  <div className="ml-4">
                    <i>
                      Exibindo {listaMaterialFiltrada?.length ?? 0} de{' '}
                      {listaMaterial
                        ? listaMaterial.filter(item =>
                            mostrarDesativados ? true : item.isAtivo === true,
                          )?.length ?? 0
                        : 0}{' '}
                      registros
                    </i>
                  </div>
                </Col>
                <Col sm="4">
                  <BuscarRegras
                    className="float-right mr-2"
                    id="outlined-basic"
                    label="Buscar"
                    variant="outlined"
                    size="small"
                    type="text"
                    onChange={event => {
                      filtroTabela(event);
                    }}
                  />
                </Col>
              </Row>

              {renderTabelaConteudo()}
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default Materiais;
